/**
 * FilmCompsContainer Component
 * Main container for managing film comps data and coordinating child components
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Alert, CircularProgress, Tabs, Tab } from '@mui/material';
import { Assignment } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useFilmComps } from '../../hooks/useFilmComps';
import CompTabs from './CompTabs';
import CompsList from './CompsList';
import CompsSummary from './CompsSummary';
import CompReportContainer from './CompReport/CompReportContainer';
import { formatErrorMessage } from '../../lib/utils/compFormatters';

const FilmCompsContainer = ({ filmId, filmTitle }) => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('greenlight');
  const [mainTab, setMainTab] = useState(0); // 0 = View, 1 = Comp Report

  // Use the film comps hook - only fetch greenlight comps
  // NOTE: Modeled comps have been temporarily removed.
  // They may be re-added in the future when the model improves.
  const {
    comps,
    loading,
    error,
    refreshComps,
    assignComp,
    updateComp,
    deleteComp,
    clearError,
    isLoaded,
    hasError,
    isEmpty,
    totalCount,
    greenlightCount,
    metadata
  } = useFilmComps(filmId, {
    type: 'greenlight', // Only fetch greenlight comps
    autoFetch: true,
    filmTitle
  });

  // Handle tab change
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
  };

  // Handle refresh
  const handleRefresh = () => {
    refreshComps();
  };

  // Handle error clear
  const handleClearError = () => {
    clearError();
  };

  const handleViewDetails = (comp) => {
    console.log('View details for comp:', comp.id);
    // TODO: Implement comp details modal
  };

  const canManageComps = false; // Read-only in Film Detail pages

  const isSampleData = metadata?.isSample;

  if (loading && !isLoaded) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 200,
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Loading film comps...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (hasError) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          onClose={handleClearError}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Error Loading Film Comps
          </Typography>
          <Typography variant="body2">
            {formatErrorMessage(error)}
          </Typography>
        </Alert>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <button 
            onClick={handleRefresh}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </Box>
      </Paper>
    );
  }

  // Empty state for read-only users
  if (isEmpty && !canManageComps) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 2, color: 'text.secondary' }}>
          🎬 No Film Comps Found
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          No comparisons have been found for <strong>{filmTitle}</strong> yet.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Film comps will appear here once they are generated by our AI system or assigned by analysts.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
          Film Comps
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Expert-curated film comparisons for <strong>{filmTitle}</strong>
        </Typography>
      </Box>

      {isSampleData && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Showing representative Film Comps data while the live comparison service finishes setup.
        </Alert>
      )}


      {/* Main Tabs (View/Comp Report) */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={mainTab}
          onChange={(e, newValue) => setMainTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label="View Comps" 
            icon={<Assignment />}
            iconPosition="start"
          />
          <Tab 
            label="Comp Report" 
            icon={<Assignment />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* View Tab Content */}
      {mainTab === 0 && (
        <>
          {/* Summary */}
          <CompsSummary 
            totalCount={totalCount}
            greenlightCount={greenlightCount}
            onRefresh={handleRefresh}
            loading={loading}
          />


          {/* Tabs */}
          <CompTabs 
            activeTab={activeTab}
            onTabChange={handleTabChange}
            greenlightCount={greenlightCount}
          />

          {/* Comps List */}
          <CompsList 
            comps={comps}
            loading={loading}
            onRefresh={handleRefresh}
            canManage={false}
            userProfile={userProfile}
            onEdit={null}
            onDelete={null}
            onViewDetails={handleViewDetails}
            onAddComp={null}
          />
        </>
      )}

      {/* Comp Report Tab Content */}
      {mainTab === 1 && (
        <CompReportContainer
          filmId={filmId}
          filmTitle={filmTitle}
          compFilms={(comps.greenlight || []).map(comp => ({
            compFilmId: comp.compFilmId,
            compFilmTitle: comp.compFilmTitle || comp.compFilmId
          }))}
        />
      )}
    </Box>
  );
};

export default FilmCompsContainer;
